import { clsx } from 'clsx'
import { useEffect, useRef, type ReactNode } from 'react'
import { X } from './icons'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  /** 'sheet' docks to the right edge for staff side panels. */
  variant?: 'center' | 'sheet'
  size?: 'md' | 'lg'
}

/** Native <dialog> wrapper: focus trap, Esc, backdrop click, inert background for free. */
export function Modal({ open, onClose, title, children, footer, variant = 'center', size = 'md' }: Props) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (open && !el.open) el.showModal()
    if (!open && el.open) el.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose()
      }}
      aria-labelledby="modal-title"
      className={clsx(
        'bg-paper text-charcoal shadow-[0_24px_64px_rgb(0_0_0/0.25)] backdrop:bg-eerie/60',
        variant === 'center' && clsx('m-auto w-[calc(100%-2rem)]', size === 'md' ? 'max-w-lg' : 'max-w-3xl'),
        variant === 'sheet' && 'mr-0 ml-auto h-dvh max-h-dvh w-full max-w-xl',
      )}
    >
      <div className={clsx('flex flex-col', variant === 'sheet' && 'h-full')}>
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <h2 id="modal-title" className="text-h4">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 inline-flex h-9 w-9 items-center justify-center text-dim hover:text-ink">
            <X size={16} />
          </button>
        </header>
        <div className={clsx('px-6 py-5', variant === 'sheet' && 'flex-1 overflow-y-auto')}>{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-3 border-t border-gray-200 px-6 py-4">{footer}</footer>}
      </div>
    </dialog>
  )
}
